import { useCallback, useEffect, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useVersion } from 'react-admin';
import { Box, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { SnackbarContainer } from 'components/standalone';
import { extractErrorMessage } from 'utils';
import {
  RepairFormulaFormValues,
  RepairOption,
  RepairsSettingsContainer,
} from '../containers';
import { RepairsSettingsForm } from '../components';

const RepairsSettingsEditInner = () => {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { notify } = SnackbarContainer.useContainer();
  const {
    selectedFormula,
    isLoading,
    formulaLoaded,
    formulaError,
    formLoading,
    setFormLoading,
    loadFormulaById,
    updateFormula,
  } = RepairsSettingsContainer.useContainer();

  const version = useVersion();

  useEffect(() => {
    if (!id) return;
    loadFormulaById(id);
  }, [id, version, loadFormulaById]);

  useEffect(() => {
    if (formulaError || (formulaLoaded && !selectedFormula)) {
      notify('Element does not exist', 'error');
      history.push('/repair-formula');
    }
  }, [formulaError, formulaLoaded, selectedFormula, notify, history]);

  const selectedRepairOption = useMemo<RepairOption | null>(() => {
    if (!selectedFormula?.repair) return null;
    return {
      id: selectedFormula.repair.id,
      name: selectedFormula.repair.description,
    };
  }, [selectedFormula]);

  const noopLoadRepairOptions = useCallback(
    async (): Promise<RepairOption[]> => [],
    [],
  );

  const handleSubmit = useCallback(
    async (values: RepairFormulaFormValues) => {
      if (!id) return;
      setFormLoading(true);
      try {
        await updateFormula(id, { active: values.active });
        notify('Element updated');
        history.push('/repair-formula');
      } catch (error: unknown) {
        console.error('Repair formula update failed:', error);
        notify(
          extractErrorMessage(error, 'Failed to update repair formula'),
          'error',
        );
      } finally {
        setFormLoading(false);
      }
    },
    [id, updateFormula, history, notify, setFormLoading],
  );

  const handleGoBack = useCallback(
    () => history.push('/repair-formula'),
    [history],
  );

  if (isLoading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (!selectedFormula) {
    return null;
  }

  return (
    <Box className={classes.formContainer}>
      <RepairsSettingsForm
        mode="edit"
        initialValues={selectedFormula}
        onSubmit={handleSubmit}
        loading={formLoading}
        onGoBack={handleGoBack}
        loadRepairOptions={noopLoadRepairOptions}
        selectedRepairOption={selectedRepairOption}
      />
    </Box>
  );
};

export const RepairsSettingsEdit = () => (
  <RepairsSettingsContainer.Provider>
    <RepairsSettingsEditInner />
  </RepairsSettingsContainer.Provider>
);

const useStyles = makeStyles(theme => ({
  formContainer: {
    position: 'relative',
    boxShadow: theme.shadows[2],
    backgroundColor: theme.palette.background.paper,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
  },
}));

export default RepairsSettingsEdit;
